/// <amd-module name="Wit/Portal/WorkItemPortal" />
import * as React from "react";
import { connect } from "react-redux";
import { IModuleStore } from "redux-dynamic-modules";
import { LegacyComponent } from "VSS/Legacy/Legacy";
import { VssComponent } from "VSS/Platform/Layout";
import { SharedStoreDynamicModuleLoader } from "VSS/Redux/SharedStoreDynamicModuleLoader";
import { getNewTemporaryWorkItemId } from "Wit/WorkItems/Utilities/WorkItemUtilities";
import { configureWorkItemPortalStore } from "./Redux/ConfigureWorkItemPortalStore";
import { WorkItemPortalActions } from "./Redux/WorkItemPortalActions";
import { IPortalWorkItem, IWorkItemPortalAwareState } from "./Redux/WorkItemPortalContracts";
import { getWorkItemPortalModule } from "./Redux/WorkItemPortalModule";
import { getNextWorkItem } from "./Redux/WorkItemPortalSelectors";

interface IWorkItemPortalStateProps {
    nextWorkItem?: IPortalWorkItem;
}

interface IWorkItemPortalStateOwnProps {
    workItemId?: number;
    workItemTypeName?: string;
    onDismiss?: () => void;
}

class WorkItemPortal extends VssComponent<IWorkItemPortalStateProps & IWorkItemPortalStateOwnProps & typeof Actions> {
    public componentDidMount() {
        if (this.props.workItemId) {
            this.props.showWorkItem({
                workItemId: this.props.workItemId,
                isCreationMode: false
            });
        } else if (this.props.workItemTypeName) {
            this.props.showWorkItem({
                workItemId: this.props.workItemId || getNewTemporaryWorkItemId(),
                workItemTypeName: this.props.workItemTypeName,
                isCreationMode: true
            });
        }
        // No-op if neither work item id or work item type name are provided
    }

    public render(): JSX.Element | null {
        const { nextWorkItem } = this.props;
        if (nextWorkItem) {
            const { isCreationMode: isCreateMode, initialFieldValues, workItemId, workItemTypeName } = nextWorkItem;
            const wrappedType = isCreateMode ? "createNewWorkItemDialog" : "WorkItemDialog";
            const legacyComponentProperties = {
                modules: ["WorkItemTracking/SharedScripts/WorkItemDialogShim"],
                wrappedType,
                onDismiss: this._onDismiss
            };

            if (isCreateMode) {
                legacyComponentProperties["workItemTypeName"] = workItemTypeName;
                legacyComponentProperties["initialValues"] = initialFieldValues;
            } else if (workItemId) {
                legacyComponentProperties["workItemId"] = workItemId;
            }

            return React.createElement(LegacyComponent, legacyComponentProperties);
        }
        return null;
    }

    private _onDismiss = () => {
        if (this.props.nextWorkItem) {
            this.props.nextWorkItem.onDismiss ? this.props.nextWorkItem.onDismiss() : this.props.onDismiss && this.props.onDismiss();
            this.props.closeWorkItem(this.props.nextWorkItem.workItemId);
        }
    };
}

const Actions = {
    showWorkItem: WorkItemPortalActions.showWorkItem,
    closeWorkItem: WorkItemPortalActions.closeWorkItem
};

function mapStateToProps(state: IWorkItemPortalAwareState): IWorkItemPortalStateProps {
    return {
        nextWorkItem: getNextWorkItem(state)
    };
}

const ConnectedWorkItemPortal = connect(
    mapStateToProps,
    Actions
)(WorkItemPortal);

class DynamicWorkItemPortal extends VssComponent<IWorkItemPortalStateOwnProps> {
    public render() {
        return (
            <SharedStoreDynamicModuleLoader modules={getWorkItemPortalModule()} configureStore={this._configureStore}>
                <ConnectedWorkItemPortal {...this.props} />
            </SharedStoreDynamicModuleLoader>
        );
    }

    private _configureStore = (): IModuleStore<IWorkItemPortalAwareState> => {
        return configureWorkItemPortalStore(this.context.pageContext);
    };
}

VssComponent.register("work-item-portal-component", DynamicWorkItemPortal);
